
import Vue, { PropType } from 'vue';

import DecommissionGatewayModal from '@/components/modal/DecommissionGatewayModal.vue';
import GatewayEditModal from '@/components/modal/GatewayEditModal.vue';
import Table from '@/components/common/Table.vue';
import { Controller } from '@/interfaces/controllers';
import { DeviceAreaEdit, DeviceEdit, Floor } from '@/interfaces/locations';
import { Gateway } from '@/interfaces/gateways';
import { BvTableFieldArray } from 'bootstrap-vue/src/components/table';
import dayjs from '@/plugins/dayjs';

interface GatewayRow {
	original: Gateway;
	serialNumber: string;
	badge: {
		variant: 'success' | 'danger' | 'warning';
		text: string;
	};
	description: string;
	floor: string;
	softwareVersion: string;
	channel: string;
}

export default Vue.extend({
	name: 'GatewayList',
	components: {
		GatewayEditModal,
		DecommissionGatewayModal,
		Table,
	},
	data() {
		return {
			oldGateway: null,
			decommissioningGateway: null,
		} as {
			oldGateway: Gateway | null;
			decommissioningGateway: Gateway | null;
		};
	},
	props: {
		gateways: { type: Array as PropType<Gateway[]>, required: true },
		floors: { type: Array as PropType<Floor[]>, required: true },
		controllers: { type: Array as PropType<Controller[]>, required: true },
		userPermission: { type: Boolean, default: true },
	},
	computed: {
		fields(): BvTableFieldArray {
			return [
				{ key: 'serialNumber', label: this.$i18n.t('Serial number').toString(), sortable: true },
				{ key: 'badge', label: this.$t('State').toString(), sortable: true },
				{ key: 'description', label: this.$i18n.t('Description').toString(), sortable: false },
				{
					key: 'floor',
					label: this.$i18n.t('Floor').toString(),
					sortable: true,
				},
				{
					key: 'softwareVersion',
					label: this.$i18n.t('Software version').toString(),
					sortable: true,
				},
				{ key: 'channel', label: this.$i18n.t('Channel').toString(), sortable: true },
				{ key: 'editDelete', label: '' },
			];
		},
		gatewayList(): GatewayRow[] {
			return this.gateways.map((gateway) => {
				return {
					original: gateway,
					serialNumber: gateway.serial_number ?? '',
					badge: this.gatewayInfo(gateway),
					description: gateway.description,
					floor: this.gatewayFloor(gateway),
					softwareVersion: this.gatewaySoftwareVersion(gateway),
					channel: this.gatewayChannel(gateway),
				};
			});
		},
	},
	methods: {
		addGatewayModal() {
			this.oldGateway = null;
			this.$bvModal.show('modal-gateway');
		},
		addGateway(newGateway: DeviceEdit, newDeviceArea?: DeviceAreaEdit) {
			this.$emit('addGateway', newGateway, newDeviceArea);
			this.$bvModal.hide('modal-gateway');
		},
		modifyGateway(gatewayId: number, newGateway: DeviceEdit, newDeviceArea?: DeviceAreaEdit) {
			this.$emit('modifyGateway', gatewayId, newGateway, newDeviceArea);
			this.$bvModal.hide('modal-gateway');
		},
		decommissionGateway(gateway: Gateway) {
			this.$emit('decommissionGateway', gateway);
			this.$bvModal.hide('modal-decommission-gateway');
		},
		gatewayInfo(gateway: Gateway): { variant: 'success' | 'danger' | 'warning'; text: string } {
			const cutOff = dayjs.utc();
			const controller = this.controllers.find((c) => c.id === gateway.fk_controller);
			const announcment = controller
				? dayjs.utc(controller.dt_announcement).add(180, 'minute')
				: '';
			if (controller && announcment < cutOff) {
				return { variant: 'danger', text: this.$t('Offline').toString() };
			} else if (controller === undefined || controller.dt_announcement === null) {
				return { variant: 'warning', text: this.$t('Unknown').toString() };
			} else {
				return { variant: 'success', text: this.$t('Online').toString() };
			}
		},
		gatewayFloor(gateway: Gateway): string {
			const floor = this.floors.find((f) => f.id === gateway.fk_area);
			if (floor) {
				return floor.name;
			} else {
				return '';
			}
		},
		gatewaySoftwareVersion(gateway: Gateway): string {
			const controller = this.controllers.find((c) => c.id === gateway.fk_controller);
			if (controller) {
				return controller?.software_version ?? '';
			} else {
				return '';
			}
		},
		gatewayChannel(gateway: Gateway): string {
			const controller = this.controllers.find((c) => c.id === gateway.fk_controller);
			if (controller) {
				return String(controller?.radio_channel) ?? '';
			} else {
				return '';
			}
		},
		onRowEdit(item: GatewayRow) {
			this.oldGateway = item.original;
			this.$bvModal.show('modal-gateway');
		},
		onRowDelete(item: GatewayRow) {
			this.decommissioningGateway = item.original;
			this.$bvModal.show('modal-decommission-gateway');
		},
		onNavigate(item: GatewayRow) {
			this.$emit('navigationGateway', item.original.id.toString());
		},
	},
});
